import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import {
  Heart, PawPrint, CheckCircle2, Share2, Copy, ExternalLink, Loader2, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_COPY = {
  active: {
    eyebrow: "Sponsor ad · Live",
    title: "Thank you for protecting animals.",
    body:
      "Your $100 sponsor donation keeps Paws on Record running — and keeps your business in front of shelters, rescues, and adoption-minded families nationwide. Your ad is live in the registry sidebar right now.",
    accent: "bg-green-500",
  },
  pending: {
    eyebrow: "Sponsor ad · Under review",
    title: "Thanks for signing up as a sponsor.",
    body:
      "We're reviewing your creative — usually within 24 hours. Once approved, send your $100 donation and we'll mark it live. Your ad stays saved on your dashboard in the meantime.",
    accent: "bg-amber-400",
  },
  pending_payment: {
    eyebrow: "Sponsor ad · Awaiting donation",
    title: "You're approved — thank you!",
    body:
      "Your ad is reviewed and approved. Send a $100 donation via Cash App, Zelle, or PayPal (details below) and we'll publish it in the sidebar within 24 hours.",
    accent: "bg-amber-400",
  },
  paused: {
    eyebrow: "Sponsor ad · Paused",
    title: "Your sponsor slot has expired.",
    body:
      "Donate $100 again whenever you're ready and we'll put your ad back up for another 30 days. Your campaign and creative are still saved on your dashboard — nothing was deleted.",
    accent: "bg-zinc-400",
  },
  rejected: {
    eyebrow: "Sponsor ad · Needs changes",
    title: "We need a quick revision.",
    body:
      "Our team flagged your creative for revision — please check your dashboard for the admin note, edit, and resubmit. Email us if you're unsure what to change.",
    accent: "bg-red-500",
  },
};

export default function ThankYou() {
  const { id } = useParams();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    api
      .get(`/campaigns/public/${id}`)
      .then(({ data }) => { if (alive) setCampaign(data); })
      .catch(() => { if (alive) setError(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [id]);

  const share = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      toast.success("Link copied — share with your team!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy link");
    }
  };

  if (loading) {
    return <div className="min-h-[40vh] flex items-center justify-center"><Loader2 className="animate-spin" /></div>;
  }

  if (error || !campaign) {
    return (
      <div className="max-w-md mx-auto px-6 py-20 text-center" data-testid="thanks-not-found">
        <h1 className="font-display font-black text-3xl">We couldn't find that campaign</h1>
        <p className="mt-3 text-zinc-600">
          The link may be outdated. Head to your dashboard to see your active sponsor ads.
        </p>
        <Link
          to="/my-campaigns"
          className="mt-6 inline-flex items-center gap-2 bg-zinc-900 text-white px-5 py-3 font-bold uppercase tracking-widest text-sm hover:bg-red-600 transition-colors"
        >
          Go to my dashboard
        </Link>
      </div>
    );
  }

  const status = STATUS_COPY[campaign.status] || STATUS_COPY.pending;
  const isLive = campaign.status === "active";
  const paidThrough = campaign.paid_through ? new Date(campaign.paid_through) : null;

  return (
    <div className="bg-white" data-testid="thanks-page">
      {/* Hero */}
      <section className="border-b-2 border-zinc-900 bg-gradient-to-br from-amber-50 to-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-20 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-10 items-start">
          <div>
            <div className="inline-flex items-center gap-2 border border-zinc-900 bg-white px-3 py-1.5 text-[11px] uppercase tracking-widest font-bold mb-6">
              <span className={`inline-block w-2 h-2 rounded-full ${status.accent} ${isLive ? "animate-pulse" : ""}`} aria-hidden="true" />
              {status.eyebrow}
            </div>
            <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
              {status.title}
            </h1>
            <p className="mt-6 text-lg text-zinc-700 max-w-2xl leading-relaxed">
              {status.body}
            </p>

            {isLive && paidThrough && (
              <div className="mt-6 inline-flex items-center gap-2 border-l-4 border-green-500 bg-green-50 px-4 py-3 text-sm" data-testid="thanks-live-through">
                <CheckCircle2 size={16} className="text-green-700" />
                <div>
                  Your ad is <strong>live now</strong> and runs through <strong>{paidThrough.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}</strong>.
                </div>
              </div>
            )}

            <div className="mt-8 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={share}
                className="inline-flex items-center gap-2 bg-zinc-900 text-white px-5 py-3 font-bold uppercase tracking-widest text-sm hover:bg-red-600 transition-colors"
                data-testid="thanks-share-btn"
              >
                {copied ? <Copy size={14} /> : <Share2 size={14} />} {copied ? "Link copied" : "Share this page"}
              </button>
              <Link
                to="/my-campaigns"
                className="inline-flex items-center gap-2 border-2 border-zinc-900 px-5 py-3 font-bold uppercase tracking-widest text-sm hover:bg-zinc-900 hover:text-white transition-colors"
                data-testid="thanks-dashboard-btn"
              >
                Go to my dashboard
              </Link>
            </div>
          </div>

          {/* Ad preview */}
          <aside className="border-2 border-zinc-900 bg-white shadow-[8px_8px_0_0_#18181b]" data-testid="thanks-ad-preview">
            <div className="px-4 py-2 border-b border-zinc-200 flex items-center justify-between">
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                Preview · Sidebar slot
              </div>
              {isLive && (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-green-700">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" /> Live
                </span>
              )}
            </div>
            <div className="aspect-[4/3] bg-zinc-100 overflow-hidden">
              <img
                src={campaign.ad_image_url}
                alt={campaign.ad_title}
                className="w-full h-full object-cover"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            </div>
            <div className="p-4">
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                {campaign.advertiser_name}
              </div>
              <div className="font-display font-bold text-base leading-snug mt-1">
                {campaign.ad_title}
              </div>
              <a
                href={campaign.ad_link_url}
                target="_blank"
                rel="noreferrer"
                className="mt-3 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-red-600 hover:underline"
              >
                Preview link <ExternalLink size={11} />
              </a>
            </div>
          </aside>
        </div>
      </section>

      {/* Impact band */}
      <section className="border-b border-zinc-200">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-14 grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
          <Impact
            icon={<PawPrint size={18} />}
            num="251+"
            label="Convicted records you help surface"
            body="Every shelter that checks this registry before placing an animal is one adopter less likely to land in the wrong hands."
          />
          <Impact
            icon={<ShieldCheck size={18} />}
            num="50"
            label="U.S. states + D.C. we cover"
            body="Your donation keeps Paws on Record free, searchable, and independent — no paywalls, no gated records."
          />
          <Impact
            icon={<Heart size={18} className="fill-red-600 text-red-600" />}
            num="100%"
            label="Goes to platform + outreach"
            body="We're a small team. Donations fund hosting, email, and the legal backbone that protects takedown requests."
          />
        </div>
      </section>

      {/* Donate again */}
      <section className="bg-zinc-900 text-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 py-16 text-center">
          <div className="label-eyebrow text-amber-300 mb-3">Give again when you're ready</div>
          <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight">
            No subscription. No auto-charge. Just when you want to.
          </h2>
          <p className="mt-4 text-zinc-300 max-w-xl mx-auto leading-relaxed">
            Whenever you'd like to renew your sponsor slot for another 30 days, send <strong>$100</strong> to one of:
          </p>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3 text-left">
            <PayRail name="Cash App" handle="$imnono7733" testId="thanks-pay-cashapp" />
            <PayRail name="Zelle" handle="info@pawsonrecord.com" testId="thanks-pay-zelle" />
            <PayRail name="PayPal" handle="info@pawsonrecord.com" testId="thanks-pay-paypal" />
          </div>
          <div className="mt-6 text-xs font-mono text-zinc-400 break-all">
            Campaign ID for your donation memo: <span className="text-amber-300 select-all" data-testid="thanks-campaign-id">{campaign.id}</span>
          </div>
        </div>
      </section>
    </div>
  );
}

function Impact({ icon, num, label, body }) {
  return (
    <div>
      <div className="flex items-center gap-2 text-zinc-900">
        {icon}
        <div className="font-display font-black text-3xl tracking-tighter">{num}</div>
      </div>
      <div className="text-[11px] mt-1 font-mono uppercase tracking-widest text-zinc-500">{label}</div>
      <p className="mt-3 text-sm text-zinc-700 leading-relaxed">{body}</p>
    </div>
  );
}

function PayRail({ name, handle, testId }) {
  return (
    <div className="border border-zinc-700 bg-zinc-800 p-4">
      <div className="font-display font-bold text-sm">{name}</div>
      <div className="font-mono text-amber-300 text-sm mt-1 select-all break-all" data-testid={testId}>{handle}</div>
    </div>
  );
}
